import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';

export type SectionProgressProps = {
  total: number;
  currentIndex: number;
};

const CIRCLE_SIZE = 35;
const CONNECTOR_WIDTH = 100;
const BLOCK_MARGIN = -15;

const SectionProgress: React.FC<SectionProgressProps> = ({
  total,
  currentIndex,
}) => {
  const { t } = useTranslation(TranslationsKeys.SURVEY);
  const items = Array.from({ length: total });
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [lineBreaks, setLineBreaks] = useState<boolean[]>([]);

  // Calcular los line-breaks, para evitar lineas que no se conectan a círculos
  useEffect(() => {
    const checkLineBreaks = () => {
      const newLineBreaks: boolean[] = [];

      itemRefs.current.forEach((ref, index) => {
        if (!ref || index === 0) {
          return;
        }

        const currentTop = ref.getBoundingClientRect().top;
        const previousTop =
          itemRefs.current[index - 1]?.getBoundingClientRect().top || 0;

        newLineBreaks[index - 1] = Math.abs(currentTop - previousTop) > 5;
      });

      setLineBreaks(newLineBreaks);
    };

    checkLineBreaks();
    window.addEventListener('resize', checkLineBreaks);

    return () => window.removeEventListener('resize', checkLineBreaks);
  }, [total]);

  return (
    <Row className="d-flex flex-wrap justify-content-center align-items-center">
      {items.map((_, i) => (
        <div
          key={i}
          ref={(el) => (itemRefs.current[i] = el)}
          className="d-flex flex-column align-items-center"
          style={{
            marginRight: i < total - 1 ? CONNECTOR_WIDTH + BLOCK_MARGIN : 0,
            position: 'relative',
          }}
        >
          {/* Círculo y Etiqueta */}
          <div className="d-flex flex-column align-items-center">
            <div
              style={{
                width: CIRCLE_SIZE,
                height: CIRCLE_SIZE,
                borderRadius: '50%',
                boxShadow:
                  i <= currentIndex
                    ? '0 0 0 6px #ffffff, 0 0 0 7px var(--primary)'
                    : '0 0 0 6px #ffffff, 0 0 0 7px #F4F4F4',
                border: '1px solid var(--primary)',
                backgroundColor:
                  i <= currentIndex ? 'var(--primary)' : 'transparent',
                color: i <= currentIndex ? '#fff' : 'var(--primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                marginBottom: 8,
              }}
            >
              {i + 1}
            </div>
            <span style={{ color: 'var(--primary)', fontSize: 14 }}>
              {t(`sectionProgress.section`)} {i + 1}
            </span>
          </div>

          {/* Línea conectora */}
          {i < total - 1 && !lineBreaks[i] && (
            <div
              style={{
                position: 'absolute',
                top: CIRCLE_SIZE / 2,
                right: -CONNECTOR_WIDTH - BLOCK_MARGIN / 2 - 4,
                width: CONNECTOR_WIDTH,
                height: 1,
                backgroundColor:
                  i < currentIndex ? 'var(--primary)' : '#F4F4F4',
              }}
            />
          )}
        </div>
      ))}
    </Row>
  );
};

export default SectionProgress;
